import { useEffect, useState } from "react";
import { CalendarClock, Plus, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StationLogo, LiveDot } from "@/components/radio/StationLogo";
import { activeOccurrence } from "@/components/radio/RadioScheduler";
import { api, formatApiError } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const changed = () => window.dispatchEvent(new Event("radio-schedules-changed"));

const DayPicker = ({ value, onChange, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {DAYS.map((d, i) => (
      <button key={d} type="button" onClick={() => onChange(value.includes(i) ? value.filter((x) => x !== i) : [...value, i].sort())}
        data-testid={`${testid}-${i}`}
        className={`h-8 w-10 rounded-md text-xs transition-colors ${value.includes(i) ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 ring-1 ring-zinc-200 hover:ring-zinc-400"}`}>{d}</button>
    ))}
  </div>
);

export const Schedules = ({ stations }) => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ station_id: "", days: [0, 1, 2, 3, 4], start: "07:00", end: "09:00" });
  const [, setTick] = useState(0);
  const load = () => api.get("/radio/schedules").then((r) => setItems(r.data));
  useEffect(() => { load(); const t = setInterval(() => setTick((x) => x + 1), 30000); return () => clearInterval(t); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.station_id) { toast.error("Pick a station"); return; }
    if (!form.days.length) { toast.error("Pick at least one day"); return; }
    try { await api.post("/radio/schedules", form); toast.success("Schedule added"); load(); changed(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const patch = async (s, upd) => { await api.patch(`/radio/schedules/${s.id}`, upd); load(); changed(); };
  const del = async (s) => { await api.delete(`/radio/schedules/${s.id}`); load(); changed(); };

  if (!stations.length) return <p className="text-sm text-zinc-500" data-testid="schedule-no-stations">Save a station first (Discover tab or "Add stream link"), then schedule it here.</p>;
  return (
    <div className="max-w-3xl">
      <form onSubmit={add} className="rounded-lg border border-zinc-200 bg-white p-6" data-testid="schedule-form">
        <h3 className="flex items-center gap-2 text-lg font-medium"><CalendarClock size={17} className="text-amber-600" />New listening slot</h3>
        <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_auto_auto]">
          <Select value={form.station_id} onValueChange={(v) => setForm({ ...form, station_id: v })}>
            <SelectTrigger className="h-10" data-testid="schedule-station-select"><SelectValue placeholder="Choose a station" /></SelectTrigger>
            <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="h-10 w-32" required data-testid="schedule-start-input" />
          <Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="h-10 w-32" required data-testid="schedule-end-input" />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <DayPicker value={form.days} onChange={(days) => setForm({ ...form, days })} testid="schedule-days" />
          <button type="submit" data-testid="schedule-add-button" className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm text-white"><Plus size={14} />Add slot</button>
        </div>
      </form>
      <p className="mt-4 flex items-start gap-2 text-xs text-zinc-500"><Info size={13} className="mt-0.5 shrink-0" />The station switches on at the start time and stops at the end, in your local time, while Resonance is open in a browser tab.</p>

      <div className="mt-8 space-y-3" data-testid="schedule-list">
        {items?.length === 0 && <p className="text-sm text-zinc-500" data-testid="schedule-empty">No slots yet.</p>}
        {items?.map((s) => {
          const onAir = s.enabled && activeOccurrence(s);
          return (
            <div key={s.id} data-testid={`schedule-item-${s.id}`} className={`flex flex-wrap items-center gap-4 rounded-lg border bg-white p-4 ${onAir ? "border-red-300" : "border-zinc-200"} ${s.enabled ? "" : "opacity-60"}`}>
              <StationLogo src={s.station.favicon} className="h-11 w-11" iconSize={16} />
              <div className="min-w-[140px] flex-1">
                <p className="flex items-center gap-2 text-sm font-medium">{s.station.name}{onAir && <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-red-600" data-testid={`schedule-on-air-${s.id}`}><LiveDot />On air</span>}</p>
                <p className="font-mono text-xs text-zinc-500">{s.start}–{s.end}{s.end < s.start ? " (overnight)" : ""}</p>
              </div>
              <DayPicker value={s.days} onChange={(days) => days.length && patch(s, { days })} testid={`schedule-item-days-${s.id}`} />
              <Switch checked={s.enabled} onCheckedChange={(v) => patch(s, { enabled: v })} data-testid={`schedule-toggle-${s.id}`} />
              <button onClick={() => del(s)} data-testid={`schedule-delete-${s.id}`} className="rounded p-1.5 text-zinc-300 hover:text-red-600"><Trash2 size={15} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
